import {
    type PatientManualProvisionOptions,
    useProvisionPatientManually,
} from "api/clients/useProvisionPatientManually";
import { pick } from "radashi";
import type { FormInstance } from "antd";
import type { PatientManualProvisionRequest, PatientUpdateForm } from "api/requests";

/**
 * Build a Patient Provisioning to submit to the API, based on manual data entry.
 * @param paymentForm The form containing user data to submit.
 * @returns Request data, or undefined if the form is not ready for submission.
 */
async function buildRequest(paymentForm: FormInstance<PatientUpdateForm>) {
    try {
        // Perform validation first - this may throw an error if fields are invalid, which is fine.
        const formValues = await paymentForm.validateFields();
        const request = pick(formValues, [
            "familyName",
            "givenName",
            "phone",
            "email",
            "requestChannel",
            "allowCommunication",
        ]);
        return request satisfies PatientManualProvisionRequest;
    } catch {
        // If validation fails, it will display error messages for the user, so we don't need to do anything else.
        return undefined;
    }
}

/**
 * Hook to provision a P/RP using data supplied by a form.
 * @param paymentForm The form to obtain data from.
 * @param options Options to configure the submission behavior.
 * @returns React Query mutation properties, but with a submit function instead of a mutate function.
 */
export function useManualProvisioningSubmission(
    paymentForm: FormInstance<PatientUpdateForm>,
    options: Omit<PatientManualProvisionOptions, "mutationFn">,
) {
    const { mutate, isPending, data } = useProvisionPatientManually(options);

    const submit = async () => {
        const request = await buildRequest(paymentForm);
        if (request) {
            mutate(request);
        }
    };

    return { isPending, data, submit } as const;
}
